<template>
  <div
    ref="miniMenuRef"
    class="mini-menu"
    :class="{
      'mini-menu__one-button': numberOfButtons === 1,
      'mini-menu__three-buttons': numberOfButtons === 3
    }"
  >
    <ButtonText
      v-if="hasDownloadButton"
      class="mini-menu__button"
      :iconLeft="iconDownload"
      :buttonText="$t('general__text__download')"
      size="small"
      @onClicked="$emit('download')"
    />
    <ButtonText
      v-if="hasEditButton"
      class="mini-menu__button"
      :iconLeft="iconEdit"
      :buttonText="$t('general__text__edit')"
      size="small"
      @onClicked="$emit('edit')"
    />
    <ButtonText
      v-if="hasArchiveButton"
      class="mini-menu__button"
      :iconLeft="iconArchive"
      :buttonText="$t('general__text__archive')"
      size="small"
      @onClicked="$emit('archive')"
    />
    <ButtonText
      v-if="hasDeleteButton"
      class="mini-menu__button"
      :iconLeft="iconTrash"
      :buttonText="$t('general__text__delete')"
      size="small"
      @onClicked="$emit('delete')"
    />
  </div>
</template>
<script setup lang="ts">
import { useStore } from 'vuex'
import { onMounted, onUnmounted, ref, toRefs, watch } from 'vue'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import iconTrash from '@/design-system/assets/icons/Icon-Trash-2.svg'
import iconDownload from '@/design-system/assets/icons/Icon-Download.svg'
import iconEdit from '@/design-system/assets/icons/Icon-Edit-Medium.svg'
import iconArchive from '@/design-system/assets/icons/Icon-Archive.svg'
type Props = {
  position: {
    x: number
    y: number
  } | null
  hasArchiveButton?: boolean
  hasDeleteButton?: boolean
  hasDownloadButton?: boolean
  hasEditButton?: boolean
  numberOfButtons: number
}
const props = withDefaults(defineProps<Props>(), {
  hasEdit: false,
  hasDownload: false,
  hasArchiveButton: false,
  hasDeleteButton: false,
  numberOfButtons: 2
})
const { position } = toRefs(props)
type Emits = {
  (e: 'archive'): void
  (e: 'close'): void
  (e: 'download'): void
  (e: 'delete'): void
  (e: 'edit'): void
}
const emits = defineEmits<Emits>()
const store = useStore()

const clickCounter = ref(1)
const miniMenuRef = ref<HTMLElement | null>(null)

function close(event: MouseEvent | TouchEvent) {
  // Prevents the menu from closing when clicking on the menu button
  if (clickCounter.value > 0) {
    clickCounter.value--
    return
  }
  const target = event.target as HTMLElement
  if (!miniMenuRef.value?.contains(target)) {
    emits('close')
  }
}
function setNewMenuPosition() {
  const menu = miniMenuRef.value
  if (!menu || !position.value) return
  menu.style.left = store.state.mobileScreen
    ? position.value.x - 22 + 'px'
    : position.value.x - 118 + 'px'
  menu.style.top = store.state.mobileScreen
    ? position.value.y - 36 + 'px'
    : position.value.y + 4 + 'px'
}
watch(position, (newPos) => {
  if (newPos) setNewMenuPosition()
})
onMounted(() => {
  document.addEventListener('click', close)
  if (position.value) setNewMenuPosition()
})
onUnmounted(() => document.removeEventListener('click', close))
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.mini-menu {
  align-items: center;
  background-color: #fff;
  border: 1px solid variables.$color-greyscale-line-grey;
  border-radius: 12px;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.16);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: variables.$gap-small;
  height: 96px;
  justify-content: center;
  padding: variables.$padding-medium variables.$padding-xlarge;
  position: absolute;
  right: 0;
  top: 17px;
  width: 145px;
  z-index: variables.$z-index-overlay;
  &__button {
    width: 120px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
    padding: 8px;
    color: variables.$color-greyscale-content;
  }
  &__one-button {
    height: 64px;
  }
  &__three-buttons {
    height: 128px;
  }
}
</style>
